import React from 'react'
import { useTranslation } from 'react-i18next'
import { makeStyles } from '@material-ui/core/styles'
import withWidth, { isWidthUp } from '@material-ui/core/withWidth'

import Box from '@material-ui/core/Box'
import Avatar from '@material-ui/core/Avatar'
import Paper from '@material-ui/core/Paper'
import Typography from '@material-ui/core/Typography'
import Grid from '@material-ui/core/Grid'
import CircularProgress from '@material-ui/core/CircularProgress'
import InputAdornment from '@material-ui/core/InputAdornment'
import IconButton from '@material-ui/core/IconButton'
import ClickAwayListener from '@material-ui/core/ClickAwayListener'
import Autocomplete from '@material-ui/lab/Autocomplete'
import Chip from '@material-ui/core/Chip'
import Switch from '@material-ui/core/Switch'
import FormControlLabel from '@material-ui/core/FormControlLabel'
import { withStyles } from '@material-ui/core/styles'

import { format, parse, subDays, addDays } from 'date-fns'

import FertilizerPerformanceMap from './FertilizerPerformanceMap'

import TextField from '@material-ui/core/TextField'
import Button from '@material-ui/core/Button'
import Slider from '@material-ui/core/Slider'
import CloudDownloadIcon from '@material-ui/icons/CloudDownload'
import AddIcon from '@material-ui/icons/Add'

import {
	handleDownloadShapefile,
	multipartclient,
} from '../../../../../adapters/api-client'

import { find, uniq } from 'lodash'
import UploadGeoFiles from './UploadGeoFiles'
import PerformanceMapList from './PerformanceMapList'
import { usePerformanceZoning } from '@graniot/common/adapters/sentinel'
import CenteredProgress from '../../../../../utils/centeredprogress'
import { usePerformanceMap } from '../../../../../adapters/performance_map'
import { useTransferToJohnDeereOC } from '../../../../../adapters/johndeereoc'
import { useAuth } from '../../../../../context/AuthContext'
import GraniotPremiumButton from '@graniot/common/utils/button'

const useStyles = makeStyles((theme) => ({
	imgParcel: {
		maxHeight: 400,
		padding: theme.spacing(1),
	},
	imgContainer: {
		minHeight: 400,
	},
	divider: {
		margin: theme.spacing(2),
	},
	pickers: {
		marginTop: theme.spacing(2),
	},
	loading: {
		marginBottom: theme.spacing(20),
		marginTop: theme.spacing(20),
	},
	small: {
		width: theme.spacing(4),
		marginLeft: theme.spacing(0.5),
		//height: theme.spacing(4),
		[theme.breakpoints.down('sm')]: {
			width: theme.spacing(3),
			//height: theme.spacing(3),
		},
	},
	fixGridSwitch: {
		marginLeft: theme.spacing(1.5),
	},
}))

const AntSwitch = withStyles((theme) => ({
	root: {
		width: 28,
		height: 16,
		padding: 0,
		display: 'flex',
		marginLeft: theme.spacing(0.5),
		marginRight: theme.spacing(1),
	},
	switchBase: {
		padding: 2,
		color: theme.palette.common.white,
		'&$checked': {
			transform: 'translateX(12px)',
			color: theme.palette.common.white,
			'& + $track': {
				opacity: 1,
				backgroundColor: theme.palette.primary.main,
				borderColor: theme.palette.primary.main,
			},
		},
	},
	thumb: {
		width: 12,
		height: 12,
		boxShadow: 'none',
	},
	track: {
		border: `1px solid ${theme.palette.grey[500]}`,
		borderRadius: 16 / 2,
		opacity: 1,
		backgroundColor: theme.palette.primary.main,
	},
	checked: {},
}))(Switch)

const borderComputerTypes = [
	{
		name: 'John Deere',
	},
	{
		name: 'Amazone',
	},
	{
		name: 'Precision Planting 20|20',
	},
	{
		name: 'Trimble',
	},
	{
		name: 'Shapefile',
	},
	{
		name: 'KML',
	},
]

export function zonesReducer(state, action) {
	if (action === undefined) return

	switch (action.type) {
		case 'rate':
			return {
				...state,
				features: state.features.map((zone) => {
					if (zone.id === action.value.zone)
						return {
							...zone,
							properties: {
								...zone.properties,
								rate: action.value.rate,
							},
						}
					return zone
				}),
			}
		case 'seed':
			return { ...action.value }
		case 'reset':
			return null
		default:
			throw new Error()
	}
}

function FertilizerPerformance({ parcel, width, farms }) {
	const classes = useStyles()
	const { t } = useTranslation()
	const { loggedUser, isClientPaying } = useAuth()
	const [files, setFiles] = React.useState(null)
	const [errorRate, setErrorRate] = React.useState(false)
	const [performanceFields, setPerformanceFields] = React.useState(null)
	const [performanceMaps, setPerformanceMaps] = React.useState([])
	const [performanceMap, setPerformanceMap] = React.useState(null)
	const [weightField, setWeightField] = React.useState(null)
	const [uploadPerformanceMap, setUploadPerformanceMap] =
		React.useState(false)

	const [boderComputer, setBoderComputer] = React.useState({
		name: 'John Deere',
	})
	const [zones, setZones] = React.useState(3)
	const [zonedGeom, setZonedGeom] = React.useReducer(zonesReducer, null)

	const [baseMap, setBaseMap] = React.useState(null)

	const { isLoading, data, refetch } = usePerformanceMap(parcel.id)
	const transferToJohnDeereOC = useTransferToJohnDeereOC()

	const [metric, setMetric] = React.useState('kg')

	const handleMetricChange = (e) => {
		e.target.checked ? setMetric('kg') : setMetric('l')
	}

	React.useEffect(() => {
		refetch()
	}, [parcel])

	React.useEffect(() => {
		if (data) {
			setPerformanceMaps(data)
		}
	}, [data])

	const mutatePerformanceZoning = usePerformanceZoning()

	React.useEffect(() => {
		if (weightField) {
			setZonedGeom({ type: 'reset' })
			mutatePerformanceZoning.mutate({
				weight_field: weightField,
				parcel_id: parcel.id,
				zones: zones,
				performance_map_id: performanceFields.performance_map.id,
			})
		}
	}, [weightField, zones])

	React.useEffect(() => {
		if (performanceMap) {
			setZonedGeom({ type: 'reset' })
			mutatePerformanceZoning.mutate({
				parcel_id: parcel.id,
				zones: zones,
				performance_map_id: performanceMap.id,
			})
		}
	}, [performanceMap, zones])

	React.useEffect(() => {
		if (mutatePerformanceZoning.isSuccess) {
			setZonedGeom({ type: 'seed', value: mutatePerformanceZoning.data })
			mutatePerformanceZoning.reset()
		}
	}, [mutatePerformanceZoning])

	function getTotalFertilizer() {
		return zonedGeom?.features.reduce(
			(a, geom) => geom.properties.area * geom.properties.rate + a,
			0
		)
	}

	React.useEffect(() => {
		if (getTotalFertilizer() > 0 && errorRate) {
			setErrorRate(false)
		}
	}, [zonedGeom])

	const handleZonesChange = (event, newValue) => {
		setZones(newValue)
	}

	const handleSelectedFormat = (event, newValue) => {
		setBoderComputer(newValue)
	}

	function createShapefile() {
		if (getTotalFertilizer() > 0)
			handleDownloadShapefile({
				zoned_geom: zonedGeom,
				format: boderComputer.name,
				file_name: `${parcel.properties.name}_${format(
					new Date(),
					'yyyy-LL-dd'
				)}`,
			})
		else setErrorRate(true)
	}

	function uploadToJohnDeereOC() {
		if (getTotalFertilizer() > 0)
			transferToJohnDeereOC.mutate({
				zoned_geom: zonedGeom,
				format: boderComputer.name,
				file_name: `${parcel.properties.name}_${startVegetation.date}`,
			})
		else setErrorRate(true)
	}

	const handleClickAway = () => {
		setUploadPerformanceMap(false)
	}

	return (
		<Grid container spacing={4}>
			<Grid item xs={12} sm={6}>
				<Grid
					container
					direction="row"
					style={{ marginBottom: 20 }}
					spacing={3}
				>
					<Grid item xs={12}>
						<Box
							display="flex"
							justifyContent={
								isWidthUp('sm', width) ? 'start' : 'center'
							}
						>
							<Grid container>
								<Grid item xs={12}>
									<Box
										style={{ width: '100%' }}
										display="flex"
									>
										<Box flexGrow={1}>
											<Autocomplete
												value={performanceMap}
												options={performanceMaps}
												onChange={(event, newValue) => {
													setPerformanceMap(newValue)
												}}
												getOptionLabel={(option) =>
													option.file_name
												}
												renderOption={(option) => {
													return (
														<Box
															component="li"
															sx={{
																'& > img': {
																	mr: 2,
																	flexShrink: 0,
																},
															}}
														>
															{option.file_name}
														</Box>
													)
												}}
												renderInput={(params) => (
													<TextField
														{...params}
														fullWidth
														placeholder="Choose performance map"
														margin="normal"
													/>
												)}
											/>
										</Box>
										<Box>
											<Paper elevation={0}>
												<IconButton
													aria-label="add"
													onClick={() => {
														setUploadPerformanceMap(
															true
														)
													}}
												>
													<AddIcon />
												</IconButton>
											</Paper>
										</Box>
									</Box>
								</Grid>
								{uploadPerformanceMap && (
									<Grid item xs={12}>
										{!performanceFields ? (
											<ClickAwayListener
												onClickAway={handleClickAway}
											>
												<Paper>
													<Box display="flex">
														<Box flexGrow={1}>
															<UploadGeoFiles
																setData={
																	setPerformanceFields
																}
																setFiles={
																	setFiles
																}
																parcel={parcel}
															/>
														</Box>
													</Box>
												</Paper>
											</ClickAwayListener>
										) : (
											<Autocomplete
												value={weightField}
												options={
													performanceFields?.columns
												}
												onChange={(event, newValue) => {
													setWeightField(newValue)
												}}
												getOptionLabel={(option) =>
													option
												}
												renderOption={(option) => {
													return (
														<Box
															component="li"
															sx={{
																'& > img': {
																	mr: 2,
																	flexShrink: 0,
																},
															}}
														>
															{option}
														</Box>
													)
												}}
												renderInput={(params) => (
													<TextField
														{...params}
														fullWidth
														placeholder="Choose weight field"
														margin="normal"
													/>
												)}
											/>
										)}
									</Grid>
								)}
								{mutatePerformanceZoning.isLoading && (
									<Grid item xs={12}>
										<CenteredProgress />
									</Grid>
								)}
							</Grid>
						</Box>
					</Grid>
					<Grid item xs={12}>
						<Box
							display="flex"
							justifyContent={
								isWidthUp('sm', width) ? 'start' : 'center'
							}
						>
							<Grid container>
								<Grid item xs={9}>
									<Typography
										id="discrete-slider"
										gutterBottom
									>
										{t('Number of zones')}
									</Typography>
								</Grid>
								<Grid item xs={12}>
									<Slider
										value={zones}
										onChange={handleZonesChange}
										defaultValue={zones}
										aria-labelledby="discrete-slider"
										valueLabelDisplay="auto"
										step={1}
										marks
										min={1}
										max={10}
									/>
								</Grid>
							</Grid>
						</Box>
					</Grid>

					<Grid item xs={12}>
						<Box
							display="flex"
							justifyContent={
								isWidthUp('sm', width) ? 'start' : 'center'
							}
						>
							<Grid container>
								<Grid item xs={8}></Grid>
								<Grid item xs={4} spacing={0}>
									<Paper elevation={3}>
										<Typography
											component="div"
											variant="body"
										>
											<Grid
												component="label"
												container
												alignItems="center"
												spacing={1}
											>
												<Grid
													item
													className={
														classes.fixGridSwitch
													}
												>
													l/ha
												</Grid>
												<Grid item>
													<AntSwitch
														defaultChecked
														onChange={(e) =>
															handleMetricChange(
																e
															)
														}
													/>
												</Grid>
												<Grid item>kg/ha</Grid>
											</Grid>
										</Typography>
									</Paper>
								</Grid>
							</Grid>
						</Box>
					</Grid>

					<Grid item xs={12}>
						<Box
							display="flex"
							justifyContent={
								isWidthUp('sm', width) ? 'start' : 'center'
							}
						>
							<Grid container>
								<Grid item container>
									<Grid item xs={4}></Grid>
									<Grid item xs={2}>
										<Typography variant="subtitle2">
											{t('harvest-mean')}
										</Typography>
									</Grid>
									<Grid item xs={2}>
										<Typography variant="subtitle2">
											{t('hectares')}
										</Typography>
									</Grid>

									<Grid item xs={4}>
										<Typography variant="subtitle2">
											{t('Rate')}
										</Typography>
									</Grid>
								</Grid>

								{zonedGeom &&
									zonedGeom.features
										.sort((a, b) =>
											a.properties.mean >
											b.properties.mean
												? 1
												: b.properties.mean >
												  a.properties.mean
												? -1
												: 0
										)
										.map((geom, index) => (
											<Grid key={index} item container>
												<Grid item xs={4}>
													<Typography
														id="discrete-slider"
														gutterBottom
													>
														{t('zone')} {geom.id}
													</Typography>
												</Grid>
												<Grid item xs={2}>
													<Chip
														label={geom.properties.mean?.toFixed(
															2
														)}
														style={{
															backgroundColor:
																geom.properties
																	.color,
														}}
													/>
												</Grid>
												<Grid item xs={2}>
													<Typography
														id="discrete-slider"
														gutterBottom
													>
														{geom.properties.area?.toFixed(
															2
														)}{' '}
														(
														{(
															(geom.properties.area?.toFixed(
																2
															) *
																100) /
															zonedGeom.features.reduce(
																(a, c) =>
																	a +
																	c.properties
																		.area,
																0
															)
														).toFixed(0)}
														%)
													</Typography>
												</Grid>

												<Grid item xs={4}>
													<TextField
														name="harvested_qty"
														type="number"
														fullWidth
														error={errorRate}
														size="small"
														value={
															geom.properties.rate
														}
														onChange={(e) =>
															setZonedGeom({
																type: 'rate',
																value: {
																	zone: geom.id,
																	rate: e
																		.target
																		.value,
																},
															})
														}
														InputProps={{
															endAdornment: (
																<InputAdornment position="start">
																	{metric}/ha
																</InputAdornment>
															),
														}}
													/>
												</Grid>
											</Grid>
										))}

								<Grid item container style={{ marginTop: 5 }}>
									<Grid item xs={8}>
										<Typography variant="h6">
											{t('Total quantity')}
										</Typography>
									</Grid>

									<Grid item xs={4}>
										<Typography variant="h6">
											{zonedGeom &&
												zonedGeom.features
													.reduce(
														(a, geom) =>
															geom.properties
																.area *
																geom.properties
																	.rate +
															a,
														0
													)
													?.toFixed(2)}{' '}
											{metric}
										</Typography>
										<Typography variant="subtitle1">
											{t('fertilizers')}
										</Typography>
									</Grid>
								</Grid>
							</Grid>
						</Box>
					</Grid>

					<Grid item xs={12}>
						<Box
							display="flex"
							justifyContent={
								isWidthUp('sm', width) ? 'end' : 'center'
							}
						>
							<Autocomplete
								id="combo-box-demo"
								value={boderComputer}
								onChange={handleSelectedFormat}
								options={borderComputerTypes}
								getOptionLabel={(option) => option.name}
								style={{ width: '100%' }}
								disableClearable
								renderInput={(params) => (
									<TextField
										{...params}
										label={t('Type of computer on board')}
										size="small"
									/>
								)}
							/>
						</Box>
					</Grid>
					{isClientPaying() ||
						(loggedUser.is_superuser && (
							<Grid item xs={12}>
								<Box
									display="flex"
									justifyContent={
										isWidthUp('sm', width)
											? 'end'
											: 'center'
									}
								>
									<Button
										variant="outlined"
										color="primary"
										style={{ width: '100%' }}
										endIcon={<CloudDownloadIcon />}
										onClick={createShapefile}
										label="Download file"
										farms={farms}
									>
										{t('Download file')}
									</Button>
								</Box>
							</Grid>
						))}
					<Grid item xs={12}>
						{boderComputer.name === 'John Deere' && (
							<Box
								display="flex"
								justifyContent={
									isWidthUp('sm', width) ? 'end' : 'center'
								}
							>
								<GraniotPremiumButton
									variant="outlined"
									color="primary"
									style={{ width: '100%' }}
									action={uploadToJohnDeereOC}
									label="upload_to_johndeere_operation_center"
									farms={farms}
								/>
							</Box>
						)}
					</Grid>

					<Grid item xs={12}>
						{boderComputer.name === 'John Deere' && (
							<>
								{transferToJohnDeereOC.isSuccess && (
									<Alert
										onClose={() => {
											transferToJohnDeereOC.reset()
										}}
										severity={
											transferToJohnDeereOC.data.status
										}
									>
										{transferToJohnDeereOC.data.message}
									</Alert>
								)}
							</>
						)}
					</Grid>
				</Grid>
			</Grid>

			<Grid item xs={12} sm={6}>
				<Paper>
					<Box display="flex">
						<Box flexGrow={1}>
							<FertilizerPerformanceMap
								parcel={parcel}
								map={baseMap}
								setMap={setBaseMap}
								zonedGeom={zonedGeom}
								setZonedGeom={setZonedGeom}
							/>
						</Box>
					</Box>
				</Paper>
			</Grid>
		</Grid>
	)
}

export default withWidth()(FertilizerPerformance)
